import { uploadBufferToS3 } from '@/helper/s3.helper';
import { showToast } from '@/helper/toast.helper';
import { ArrowRight, Check, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { createWallets } from '../../store/slices/walletManagementSlice/walletSlice';
interface UploadTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UploadTransactionModal: React.FC<UploadTransactionModalProps> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [errorCsv, setErrorCsv] = useState<string>('');

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setError('');

      const input = document.getElementById('fileInput') as HTMLInputElement;
      if (input) input.value = '';
    }
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) validateFile(uploadedFile);
  };

  const validateFile = (file: File) => {
    const allowedExtensions = ['xlsx', 'xls', 'xml', 'csv'];

    const allowedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/xml',
      'application/xml',
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel',
    ];

    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      setError('Invalid file type. Only XLSX, XLS, XML, or CSV files are allowed.');
      setFile(null);
      return;
    }

    if (!allowedMimeTypes.includes(file.type)) {
      setError('Invalid file format. The file content does not match expected type.');
      setFile(null);
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 10MB limit.');
      setFile(null);
      return;
    }

    if (file.name.length > 100) {
      setError('File name is too long. Keep it under 100 characters.');
      setFile(null);
      return;
    }

    setFile(file);
    setError('');
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/sample_wallet.csv';
    link.download = 'sample_wallet.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadErrorCsv = () => {
    const blob = new Blob([errorCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invalid_transactions.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSave = () => {
    if (!file) {
      setError('Please upload a valid file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const buffer = reader.result as ArrayBuffer;
        const fileName = file.name;
        const mimeType = file.type;

        const fileUrl = await uploadBufferToS3({
          file,
          context: 'wallet',
        });
        const response = await dispatch(createWallets({ fileUrl: fileUrl.url })).unwrap();

        if (response?.errorCsv) {
          setErrorCsv(response.errorCsv);
          setError('Some transactions are invalid.');
          return;
        }

        setErrorCsv('');
        setError('');
        showToast.success('Transaction file uploaded successfully.');
        onClose();
      } catch (error: any) {
        showToast.error(error?.message || 'Failed to upload transaction file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[90px] right-0 bottom-0 z-40 flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-[#621C43] w-[90%] max-w-2xl max-h-[90vh] rounded-lg relative shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4 flex-shrink-0">
          <h2 className="text-xl text-[#FFFFFF] font-bold">Upload Transaction File</h2>
          <button
            onClick={onClose}
            className="text-[#FFFFFF] hover:text-gray-300 font-bold text-lg px-2 py-1 rounded"
          >
            <span className="text-2xl font-bold">×</span>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="text-[#FFFFFF] font-medium mb-4">
            Choose Transaction File <span className="text-red-500">*</span>
          </p>

          {/* Content */}
          <div className="grid grid-cols-1">
            {/* Template Download Card */}
            <div className="bg-[#814969] p-4 rounded-lg shadow mb-6 flex items-center gap-4">
              <img
                src="/Exclamation Triangle.png"
                alt="Download Template"
                className="w-7 h-7 object-contain mb-10"
              />
              <div>
                <p className="font-medium text-[#FFFFFF]">Download Template First</p>
                <p className="text-sm font-medium text-[#FFFFFF] mb-2">
                  Use our template to ensure your file has the correct format
                </p>
                <button
                  className="bg-[#FFF8ED33] flex gap-1 items-center font-medium text-[#FFFFFF] px-4 py-2 rounded-xl text-sm"
                  onClick={handleDownloadTemplate}
                >
                  Download Excel Template <ArrowRight size={15} strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* File Upload Card */}
            <div className="bg-[#814969] p-6 pt-3 rounded-lg text-center mb-3">
              <div className="p-2 rounded-full flex items-center justify-center">
                <img src="/upload.png" alt="upload" className="w-10 h-10" />
              </div>
              <h3 className="text-[#FFFFFF] text-xl font-bold mb-2">Upload Transaction File</h3>
              <p className="mb-2 font-medium text-[#FFFFFF]">
                Drag and drop your Excel file (.xlsx) or XML (.xml) file here or click to browse
              </p>

              <input
                type="file"
                accept=".xlsx,.xls,.xml,.csv"
                onChange={handleFileChange}
                className="hidden"
                id="fileInput"
              />
              <div className="flex items-center justify-center gap-2">
                <label
                  htmlFor="fileInput"
                  className="inline-block font-semibold cursor-pointer bg-[#4C86E7] text-white px-4 py-2 rounded-lg"
                >
                  Choose File
                </label>
                <Trash2
                  color="#FFF"
                  strokeWidth={1.5}
                  className="cursor-pointer"
                  onClick={() => {
                    setFile(null);
                    setError('');
                    (document.getElementById('fileInput') as HTMLInputElement).value = '';
                  }}
                />
              </div>

              {file && <div className="mt-2 text-sm text-white"> {file.name}</div>}
              {error && (
                <div className="mt-2">
                  <p className="text-red-500 text-sm">{error}</p>
                  {errorCsv && (
                    <button
                      onClick={handleDownloadErrorCsv}
                      className="mt-2 bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#FF5252]"
                    >
                      Download Error Report
                    </button>
                  )}
                </div>
              )}
            </div>

            <h3 className="text-[#FFFFFF] font-medium">File Requirements</h3>

            {/* Requirements */}
            <div className="bg-[#814969] p-4 rounded-lg shadow">
              <ul className="grid grid-cols-2 gap-1 text-sm text-[#FFFFFF]">
                <li className="flex items-center font-semibold gap-2">
                  <Check size={15} />
                  XLSForm or XML
                </li>
                <li className="flex items-center font-semibold gap-2">
                  <Check size={15} />
                  Valid Excel file with user details
                </li>
                <li className="flex items-center font-semibold gap-2">
                  <Check size={15} />
                  Max. file size 10MB
                </li>
                <li className="flex items-center font-semibold gap-2">
                  <Check size={15} />
                  File ID must be unique
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end px-6 py-4 border-t flex-shrink-0">
          <button
            onClick={handleSave}
            className="text-white px-10 font-semibold py-2 rounded-lg bg-[#0BA96F] hover:bg-[#09905E]"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadTransactionModal;
